import { forwardRef, useImperativeHandle, useMemo } from "react";

import { FAStringInput } from "@impero-com/common/components/form/formAtomFields/faStringInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { useFormAtom } from "@impero-com/common/utils/form/formAtom/hooks";
import {
    FormAtom,
    FormValidationError,
} from "@impero-com/common/utils/form/formAtom/types";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { FieldError, FormLabel } from "@impero-com/ui";
import { useSetAtom } from "jotai";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import { MAX_OPTION_LENGTH } from "components/fieldBuilder/consts";
import { EmptyCustomSection } from "components/fieldBuilder/emptyCustomSection";
import { CompoundFormForwardedRef } from "components/fieldBuilder/fieldBuilder";
import { FieldDrawerSeparator } from "components/fieldBuilder/fieldDrawerSeparator";
import { FieldOptionsEditor } from "components/fieldBuilder/options";

import * as theme from "./FieldCustomSection.css";
import {
    ConclusionFieldType,
    ControlConclusionField,
    handleDuplicateEffectivenessOptions,
    SampleConclusionField,
} from "./schema";

interface ConclusionFieldCustomSectionProps<
    T extends ControlConclusionField | SampleConclusionField,
> {
    fieldAtom: FormAtom<T>;
    disabled?: boolean;
}

export const ConclusionFieldCustomSection = forwardRef(
    <T extends ControlConclusionField | SampleConclusionField>(
        { fieldAtom, disabled = false }: ConclusionFieldCustomSectionProps<T>,
        ref: CompoundFormForwardedRef
    ) => {
        const { value: field } = useFormAtom(fieldAtom);
        const fieldTypeAtom = useMemo(
            () =>
                formAtom(
                    focusAtom(fieldAtom, (fa) => fa.prop("fieldType")),
                    Yup.mixed<ConclusionFieldType>().defined()
                ),
            [fieldAtom]
        );
        switch (field.fieldType.type) {
            case "FileUpload":
            case "NumericInput":
            case "TextInput":
            case "TextArea":
            case "Heading": {
                return <EmptyCustomSection ref={ref} />;
            }
            case "Effectiveness": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ConclusionFieldType, { type: "Effectiveness" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <EffectivenessEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "CheckboxGroup": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ConclusionFieldType, { type: "CheckboxGroup" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <FieldOptionsEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "RadioButtonGroup": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ConclusionFieldType, { type: "RadioButtonGroup" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <FieldOptionsEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "Dropdown": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ConclusionFieldType, { type: "Dropdown" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <FieldOptionsEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            default: {
                return assertNever(field.fieldType);
            }
        }
    }
);

const useEffectivenessFormAtom = (
    atom: FormAtom<
        Extract<
            ConclusionFieldType,
            {
                type: "Effectiveness";
            }
        >
    >
) => {
    const { t } = useTranslation();
    return useMemo(() => {
        const effectivenessOptionsSchema = Yup.object({
            effectiveOptionName: Yup.string()
                .defined()
                .required(t("FieldBuilder_Error_Option_Name_Required"))
                .trim()
                .min(1)
                .max(MAX_OPTION_LENGTH),
            partiallyEffectiveOptionName: Yup.string()
                .defined()
                .required(t("FieldBuilder_Error_Option_Name_Required"))
                .trim()
                .min(1)
                .max(MAX_OPTION_LENGTH),
            notEffectiveOptionName: Yup.string()
                .defined()
                .required(t("FieldBuilder_Error_Option_Name_Required"))
                .trim()
                .min(1)
                .max(MAX_OPTION_LENGTH),
        })
            .defined()
            .test(
                "unique-options",
                t("FieldBuilder_Error_OptionAlreadyExists"),
                (value, { createError }) => {
                    let isValid = true;
                    const duplicateKeys: string[] = [];
                    handleDuplicateEffectivenessOptions(value, (key) => {
                        duplicateKeys.push(key);
                        isValid = false;
                    });
                    if (!isValid) {
                        return createError({
                            path: "options",
                            params: { duplicateKeys },
                            message: t(
                                "FieldBuilder_Error_OptionAlreadyExists"
                            ),
                        });
                    } else {
                        return true;
                    }
                }
            );
        // atom have an empty schema at this point, let's force the correct one.
        const optionsAtom = formAtom(
            focusAtom(atom, (a) => a.prop("options")),
            effectivenessOptionsSchema,
            atom
        );
        return {
            optionsAtom,
            effectiveOptionNameAtom: formAtom(
                focusAtom(optionsAtom, (a) => a.prop("effectiveOptionName")),
                Yup.reach(
                    effectivenessOptionsSchema,
                    "effectiveOptionName"
                ) as Yup.ISchema<string>,
                optionsAtom
            ),
            partiallyEffectiveOptionNameAtom: formAtom(
                focusAtom(optionsAtom, (a) =>
                    a.prop("partiallyEffectiveOptionName")
                ),
                Yup.reach(
                    effectivenessOptionsSchema,
                    "partiallyEffectiveOptionName"
                ) as Yup.ISchema<string>,
                optionsAtom
            ),
            notEffectiveOptionNameAtom: formAtom(
                focusAtom(optionsAtom, (a) => a.prop("notEffectiveOptionName")),
                Yup.reach(
                    effectivenessOptionsSchema,
                    "notEffectiveOptionName"
                ) as Yup.ISchema<string>,
                optionsAtom
            ),
        };
    }, [atom, t]);
};

function dispatchError(error: FormValidationError | undefined) {
    const duplicateKeys: string[] =
        (error?.yup.params?.duplicateKeys as string[]) ?? [];

    return {
        baseError: duplicateKeys.length === 0 ? error : undefined,
        effectiveOptionNameError: duplicateKeys.includes("effectiveOptionName")
            ? error
            : undefined,
        partiallyEffectiveOptionNameError: duplicateKeys.includes(
            "partiallyEffectiveOptionName"
        )
            ? error
            : undefined,
        notEffectiveOptionNameError: duplicateKeys.includes(
            "notEffectiveOptionName"
        )
            ? error
            : undefined,
    };
}

const EffectivenessEditor = forwardRef(
    (
        {
            atom,
            disabled,
        }: {
            atom: FormAtom<
                Extract<ConclusionFieldType, { type: "Effectiveness" }>
            >;
            disabled: boolean;
        },
        ref: CompoundFormForwardedRef
    ) => {
        const { t } = useTranslation();
        const {
            optionsAtom,
            effectiveOptionNameAtom,
            partiallyEffectiveOptionNameAtom,
            notEffectiveOptionNameAtom,
        } = useEffectivenessFormAtom(atom);
        const { error } = useFormAtom(optionsAtom);
        const forceValidation = useSetAtom(optionsAtom.validateFieldAtom);

        useImperativeHandle(ref, () => {
            return {
                validate(force) {
                    return forceValidation(force);
                },
            };
        }, [forceValidation]);

        const styleVariant = disabled ? "borderless" : "normal";

        const {
            effectiveOptionNameError,
            partiallyEffectiveOptionNameError,
            notEffectiveOptionNameError,
            baseError,
        } = dispatchError(error);

        return (
            <>
                <FormLabel>
                    {t("TestingControlView_Effectiveness_Options")}
                </FormLabel>
                <div className={theme.effectivenessOptions}>
                    <FAStringInput
                        atom={effectiveOptionNameAtom}
                        placeholder={t(
                            "TestingProgramView_Effectiveness_EffectiveOption"
                        )}
                        styleVariant={styleVariant}
                        parentError={effectiveOptionNameError}
                        disabled={disabled}
                    />
                    <FAStringInput
                        atom={partiallyEffectiveOptionNameAtom}
                        placeholder={t(
                            "TestingProgramView_Effectiveness_PartiallyEffectiveOption"
                        )}
                        styleVariant={styleVariant}
                        parentError={partiallyEffectiveOptionNameError}
                        disabled={disabled}
                    />
                    <FAStringInput
                        atom={notEffectiveOptionNameAtom}
                        placeholder={t(
                            "TestingProgramView_Effectiveness_NotEffectiveOption"
                        )}
                        styleVariant={styleVariant}
                        parentError={notEffectiveOptionNameError}
                        disabled={disabled}
                    />
                </div>
                <FieldError error={baseError?.value} />
            </>
        );
    }
);
